import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useAuthConfig, queries, service } from "@theprelude/core";
import {
  notifySuccess,
  notifyError,
  DialogBody,
  Checkbox,
  DialogFooter,
  DialogClose,
  TrashcanIcon,
  Button,
  DialogHeader,
  DialogTitle,
} from "@theprelude/ds";
import { useState } from "react";
import { useOverlay } from "@/hooks/use-overlay";
import { DefaultDialog } from "../default-dialog";

export function DeleteTestModal(props: {
  id: string;
  open: boolean;
  onOpenChange: (value: boolean) => any;
  close: () => void;
}) {
  return (
    <DefaultDialog open={props.open} onOpenChange={props.onOpenChange}>
      <DeleteTestModalContent {...props} />
    </DefaultDialog>
  );
}

function DeleteTestModalContent(props: { id: string; close: () => void }) {
  const [purge, setPurge] = useState<boolean>(false);
  const authConfig = useAuthConfig();
  const overlay = useOverlay();
  const { data: test } = useSuspenseQuery(
    queries.detect.getTest(authConfig, props.id),
  );

  const deleteTest = useMutation({
    mutationFn: async () => {
      await service(authConfig).build.deleteTest({
        test_id: props.id,
        purge,
      });
    },
    onSuccess: async () => {
      notifySuccess({
        message: `Successfully deleted test`,
        dismissible: true,
      });
      props.close();
      void overlay.close();
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to delete test",
        error,
        dismissible: true,
      });
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Are you sure you want to delete the following test?{" "}
          {`${test.name} (*${test.id.slice(32, 36)})`}
        </DialogTitle>
      </DialogHeader>
      <DialogBody>
        <div className="flex items-center gap-4">
          <Checkbox checked={purge} onCheckedChange={() => setPurge(!purge)} />
          <span>
            (Optional) Also purge this test and its associated data. This will{" "}
            <strong>permanently</strong> delete this test and all results and
            cannot be undone.
          </span>
        </div>
      </DialogBody>
      <DialogFooter>
        <DialogClose asChild>
          <Button intent="secondary">Cancel</Button>
        </DialogClose>
        <Button
          intent="negative"
          loading={deleteTest.isPending}
          disabled={deleteTest.isPending}
          icon={<TrashcanIcon />}
          onClick={() => deleteTest.mutate()}
        >
          Delete test
        </Button>
      </DialogFooter>
    </>
  );
}
